/**
 * Production-ready error parser and formatter.
 * Converts raw Firebase, API, and network errors into clean, user-friendly messages.
 */

export interface FormattedError {
    title: string;
    message: string;
    suggestion?: string;
    actionLabel?: string;
    actionLink?: string;
}

export function formatAuthError(error: unknown): FormattedError {
    if (!error) {
        return {
            title: 'Authentication Error',
            message: 'An unexpected error occurred. Please try again.'
        };
    }

    const errCode = (error as any)?.code || '';
    const rawMessage = (error as any)?.message || (typeof error === 'string' ? error : '');

    // Extract auth code from Firebase message string if present (e.g., "Firebase: Error (auth/invalid-credential).")
    let code = errCode;
    if (!code && typeof rawMessage === 'string') {
        const match = rawMessage.match(/\(auth\/([a-z0-9-]+)\)/i);
        if (match) {
            code = `auth/${match[1]}`;
        }
    }

    switch (code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
        case 'auth/invalid-login-credentials':
            return {
                title: 'Invalid Email or Password',
                message: 'The email address or password you entered is incorrect.',
                suggestion: 'Please verify your credentials and try again.'
            };

        case 'auth/email-already-in-use':
            return {
                title: 'Email Already Registered',
                message: 'An account with this email address already exists.',
                suggestion: 'Please sign in to your existing account instead.',
                actionLabel: 'Sign in here',
                actionLink: '/login'
            };

        case 'auth/weak-password':
            return {
                title: 'Password Too Weak',
                message: 'Your password must be at least 6 characters long.',
                suggestion: 'Try using a mix of letters, numbers, and special symbols for better security.'
            };

        case 'auth/invalid-email':
            return {
                title: 'Invalid Email Format',
                message: 'Please enter a valid email address (e.g., name@domain.com).'
            };

        case 'auth/user-disabled':
            return {
                title: 'Account Suspended',
                message: 'This user account has been disabled.',
                suggestion: 'Please contact support at sagarmiishra8169@gmail.com for assistance.'
            };

        case 'auth/too-many-requests':
            return {
                title: 'Too Many Attempts',
                message: 'Access temporarily blocked due to multiple consecutive failed attempts.',
                suggestion: 'Please wait a few minutes before trying again or reset your password.'
            };

        case 'auth/network-request-failed':
            return {
                title: 'Network Connection Issue',
                message: 'Unable to connect to the server.',
                suggestion: 'Please check your internet connection and reload the page.'
            };

        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
            return {
                title: 'Sign-in Cancelled',
                message: 'The authentication window was closed before completion.'
            };

        case 'auth/requires-recent-login':
            return {
                title: 'Session Expired',
                message: 'For your security, this action requires recent authentication.',
                suggestion: 'Please sign in again to continue.',
                actionLabel: 'Sign In',
                actionLink: '/login'
            };

        case 'auth/internal-error':
            return {
                title: 'Service Temporarily Unavailable',
                message: 'Authentication service encountered a temporary problem.',
                suggestion: 'Please try again in a few moments.'
            };

        default: {
            // Clean up any remaining "Firebase: " or "Error: " prefixes
            let clean = rawMessage
                .replace(/^Firebase:\s*/i, '')
                .replace(/^Error:\s*/i, '')
                .replace(/\(auth\/[a-z0-9-]+\)\.?/gi, '')
                .trim();

            if (!clean || clean.length < 4) {
                clean = 'Something went wrong while processing your request. Please try again.';
            }

            return {
                title: 'Authentication Failed',
                message: clean
            };
        }
    }
}

/**
 * Clean error message for general API calls and background removal operations.
 */
export function formatApiError(error: unknown): string {
    if (!error) return 'An unexpected error occurred.';

    const raw = (error as any)?.response?.data?.detail ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        (typeof error === 'string' ? error : 'Request failed.');

    if (typeof raw === 'string') {
        if (raw.includes('Insufficient credits') || raw.includes('402')) {
            return 'Insufficient credits. Please upgrade your subscription plan or add credits to continue.';
        }
        if (raw.includes('Subscription expired') || raw.includes('403')) {
            return 'Your subscription plan has expired. Please renew to keep processing images.';
        }
        if (raw.includes('Invalid API Key') || raw.includes('401')) {
            return 'Invalid API key provided. Please generate a new key from your Developer dashboard.';
        }
        if (raw.includes('Network Error') || raw.includes('fetch')) {
            return 'Network error: Unable to reach the server. Please check your internet connection.';
        }
        if (raw.includes('timeout') || raw.includes('timed out')) {
            return 'The request timed out. High-resolution images may take longer; please retry.';
        }
        return raw.replace(/^Error:\s*/i, '');
    }

    return 'An unexpected error occurred. Please try again.';
}
